import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useCurrentValueGetter, useModuleName } from '../../../hooks';
import { useHistoryNavigate } from '../../../hooks/useHistoryNavigate';
import { MODULE_NAME as THIRD_PARTY_CONNECT_MODULE_NAME } from '../../../modules/3p-connect';
import {
    FEATURE_FLAGS,
    useAccount,
    useConfigMatch,
} from '../../taboola-common-frontend-modules/account-configurations';
import { userSelector } from '../../taboola-common-frontend-modules/authentication';
import { useChatAgentContext } from '../../taboola-common-frontend-modules/chat-agent/hooks/useChatAgentContext';
import { isIframeMode } from '../../taboola-common-frontend-modules/iframe/utils/isIframeMode';
import { useLocalStorage } from '../../taboola-common-frontend-modules/storage';
import { MODULE_NAME, ONBOARDING_JOURNEY_ROUTE, ONBOARDING_ROUTE } from '../config/module';
import { ON_BOARDING_JOURNEY_STATE, ONBOARDING_CHAT_DONE_KEY } from '../config/onboardingSkipKey';

const SKIP_ONBOARDING_MODULES = {
    [MODULE_NAME]: true,
    [THIRD_PARTY_CONNECT_MODULE_NAME]: true,
};

export const useOnboardingFlow = () => {
    const module = useModuleName();
    const moduleGetter = useCurrentValueGetter(module);
    const { onboardingState } = useAccount();
    const { setChatReady } = useChatAgentContext();
    const isChatEnabled = useConfigMatch({ [FEATURE_FLAGS.CHAT_AGENT_ONBOARDING_ENABLED]: 'true' });
    const isJourneyEnabled = useConfigMatch({ [FEATURE_FLAGS.ONBOARDING_JOURNEY_ENABLED]: 'true' });
    const [localStorageGetter] = useLocalStorage();
    const shouldSkipOnboarding = localStorageGetter(ONBOARDING_CHAT_DONE_KEY) === true;
    const shouldRedirectToOnboardingJourney = onboardingState === ON_BOARDING_JOURNEY_STATE;
    const iFrameMode = isIframeMode();
    const { username, hasCampaigns = true } = useSelector(userSelector);
    const isAuthorized = !!username;
    const shouldRun = isChatEnabled && !iFrameMode && !shouldSkipOnboarding && !hasCampaigns && isAuthorized;
    const shouldRunJourney = isJourneyEnabled && !iFrameMode && shouldRedirectToOnboardingJourney && isAuthorized;
    const location = useLocation();

    const navigate = useHistoryNavigate();
    useEffect(() => {
        if (!isAuthorized || SKIP_ONBOARDING_MODULES[moduleGetter()]) {
            return;
        }
        if (shouldRun) {
            navigate(ONBOARDING_ROUTE);
            return;
        }
        if (shouldRunJourney) {
            navigate(ONBOARDING_JOURNEY_ROUTE, { replace: true, useDrawer: false });
            return;
        }
        setChatReady(true);
    }, [navigate, shouldRun, setChatReady, isAuthorized, moduleGetter, shouldRunJourney, location.pathname]);
};
